import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
} from 'react-router-dom';
import './App.css';

import Home from './components/pages/Home';
import LoginAdmin from './components/pages/LoginAdmin';
import DashboardAdmin from './components/pages/DashboardAdmin';
import Categories from './components/pages/Categories';
import ProductsAdmin from './components/pages/ProductsAdmin';
import UploadProduct from './components/pages/UploadProduct';
import ProtectedRoute from './components/ProtectedRoute';
import ProductDisplay from './components/pages/ProductDisplay';
import Product from './components/pages/Product';
import Contact from './components/pages/Contact';
import Footer from './components/Footer';
import Maintenance from './components/pages/Maintenance';
import EnquiriesAdmin from './components/pages/EnquiriesAdmin';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import About from './components/pages/About';

import API_BASE_URL from './api';

function App() {
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // true = closed
    const [loading, setLoading] = useState(true);
    const [maintenanceMode, setMaintenanceMode] = useState(false);

    useEffect(() => {
        initialLoad();
        // eslint-disable-next-line
    }, []);

    const initialLoad = async () => {
        await Promise.all([checkAuthStatus(), fetchMaintenanceMode()]);
        setLoading(false);
    };

    const checkAuthStatus = async () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const response = await axios.post(`${API_BASE_URL}/api/auth/verify-token`, { token });
                if (response.data.valid) {
                    setIsAdminLoggedIn(true);
                } else {
                    setIsAdminLoggedIn(false);
                    localStorage.removeItem('token');
                    localStorage.removeItem('isAdminLoggedIn');
                }
            } catch (error) {
                setIsAdminLoggedIn(false);
                localStorage.removeItem('token');
                localStorage.removeItem('isAdminLoggedIn');
            }
        } else {
            setIsAdminLoggedIn(false);
        }
    };

    const fetchMaintenanceMode = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/maintenance`);
            setMaintenanceMode(res.data.enabled);
        } catch (err) {
            console.error('Failed to fetch maintenance mode:', err);
            setMaintenanceMode(false);
        }
    };

    const handleLogout = () => {
        setIsAdminLoggedIn(false);
        localStorage.removeItem('isAdminLoggedIn');
        localStorage.removeItem('token');
    };

    const toggleSidebar = () => setIsSidebarCollapsed(prev => !prev);

    if (loading) return <div>Loading...</div>;

    return (
        <Router>
            <AppLayout
                isAdminLoggedIn={isAdminLoggedIn}
                setIsAdminLoggedIn={setIsAdminLoggedIn}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
                handleLogout={handleLogout}
                maintenanceMode={maintenanceMode}
                setMaintenanceMode={setMaintenanceMode}
            />
        </Router>
    );
}

function AppLayout({ isAdminLoggedIn, setIsAdminLoggedIn, isSidebarCollapsed, toggleSidebar, handleLogout, maintenanceMode, setMaintenanceMode }) {
    const location = useLocation();
    const isLoginRoute = location.pathname === '/login';

    if (maintenanceMode && !isAdminLoggedIn) {
        if (isLoginRoute) {
            return (
                <div className="app-container">
                    <div className="main-content sidebar-hidden">
                        <div className="page-wrapper">
                            <Routes>
                                <Route path="/login" element={<LoginAdmin setIsAdminLoggedIn={setIsAdminLoggedIn} />} />
                            </Routes>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="app-container">
                <div className="main-content sidebar-hidden">
                    <Maintenance />
                </div>
            </div>
        );
    }

    const isAdminRoute = location.pathname.startsWith('/admin');
    const sidebarOpen = isAdminLoggedIn && !isSidebarCollapsed;
    const rootClass = `app-container${isAdminLoggedIn ? ' admin-bar-visible' : ''}`;

    return (
        <div className={rootClass}>
            <Sidebar
                isAdminLoggedIn={isAdminLoggedIn}
                isCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
                handleLogout={handleLogout}
            />

            <div className={`main-content ${isAdminLoggedIn ? (isSidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-visible') : 'sidebar-hidden'}`}>
                <Navbar
                    isAdminLoggedIn={isAdminLoggedIn}
                    toggleSidebar={toggleSidebar}
                    sidebarOpen={sidebarOpen}
                />

                <div className="page-wrapper">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<LoginAdmin setIsAdminLoggedIn={setIsAdminLoggedIn} />} />
                        <Route path="/admin/dashboard" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><DashboardAdmin maintenanceMode={maintenanceMode} setMaintenanceMode={setMaintenanceMode} handleLogout={handleLogout} /></ProtectedRoute>} />
                        <Route path="/admin/categories" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><Categories /></ProtectedRoute>} />
                        <Route path="/admin/products" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><ProductsAdmin /></ProtectedRoute>} />
                        <Route path="/admin/products/addproduct" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><UploadProduct /></ProtectedRoute>} />
                        <Route path="/admin/products/edit/:id" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><UploadProduct editMode={true} /></ProtectedRoute>} />
                        <Route path="/admin/enquiries" element={<ProtectedRoute isLoggedIn={isAdminLoggedIn}><EnquiriesAdmin /></ProtectedRoute>} />
                        <Route path="/category/:categoryId" element={<ProductDisplay />} />
                        <Route path="/search" element={<ProductDisplay />} />
                        <Route path="/product/:id" element={<Product isAdminLoggedIn={isAdminLoggedIn} />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    </Routes>
                </div>

                {!isAdminRoute && <Footer />}
            </div>
        </div>
    );
}

export default App;